import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { type FetchResult, useMutation } from "@apollo/client";

import { type AbsoluteRoute, type RouteParams } from "../../core/routes";
import { type DiscoverabilityInput } from "../../gql-generated/types";
import { getLensInfo } from "../../lens/api/queries";
import { type ApolloResult } from "../../shared/models";
import {
    type SetDiscoverabilityMutation,
    type SetDiscoverabilityMutationVariables,
    setDiscoverability as setDiscoverabilityMutation,
} from "../api";

export const useSetDiscoverability = (): ApolloResult<{
    setDiscoverability: (
        discoverabilityInput: DiscoverabilityInput,
    ) => Promise<FetchResult<SetDiscoverabilityMutation>>;
}> => {
    const { lensId } = useParams<RouteParams[AbsoluteRoute.LENS]>();

    const [mutateSetLensDiscoverabilityTags, { loading, error }] = useMutation<
        SetDiscoverabilityMutation,
        SetDiscoverabilityMutationVariables
    >(setDiscoverabilityMutation, {
        refetchQueries: lensId ? [{ query: getLensInfo, variables: { lensId } }] : [],
    });

    const setDiscoverability = useCallback(
        (discoverability: DiscoverabilityInput) => {
            if (!lensId) {
                throw new Error("Cannot update discoverability, missing Lens ID.");
            }
            return mutateSetLensDiscoverabilityTags({
                variables: {
                    lensId,
                    discoverability,
                },
            });
        },
        [mutateSetLensDiscoverabilityTags, lensId],
    );

    return { setDiscoverability, loading, error };
};
